/**
 * TODO: Add i18n
 * @see https://react.i18next.com/
 */

/**
 * TODO: Link with subscriptions API to get user active subscriptions
 * @see https://www.figma.com/design/9FuhY7T8ebPFopIgG6pNMM/Viome-App-4.0-Handoff?node-id=4396-22371&node-type=instance&m=dev
 */

import {
  Sidebar,
  SidebarMenu,
  SidebarFooter,
  SidebarHeader,
  SidebarContent,
  SidebarMenuItem,
  SidebarMenuButton,
} from '@design-system/Sidebar';
import { CheckIcon, Shield } from 'lucide-react';
import { Link } from '@design-system/Link';
import { Image } from '@design-system/Image';
import environment from '@config/environment';
import { HOME_PATH } from '@/constants/paths';
import { LOGO_WHITE } from '@/constants/images';
import { DASHBOARD_ROUTES } from './DashboardLayout.constant';
import { DashboardLayoutNavItem } from './DashboardLayoutNavItem';
import { DashboardLayoutContextSwitcher } from './DashboardLayoutContextSwitcher';
import { useBootup } from '@hooks/useBootup';
import { getKitName } from '@utils/getKitName';
import { useCancerDetectUser } from '@hooks/useCancerDetectUser';
import { usePeacockAcl } from '@hooks/usePeacockAcl';
import { useContextStore } from '@hooks/context/useContextStore';
import { useShowFormulaRecs } from '@hooks/useShowFormulaRecs';

const ACL_ADMIN_URL = `${environment.PEACOCK_EXT_URL}/acl-admin.html`;

export type DashboardLayoutSidebarProps = React.ComponentPropsWithoutRef<
  typeof Sidebar
>;

const renderedSidebarRoutes = ({
  isCancerDetectUser,
  cancerDetectClicked,
  showFormulaRecs,
}: {
  isCancerDetectUser: boolean;
  cancerDetectClicked: boolean;
  showFormulaRecs: boolean;
}) => {
  const filteredRoutes =
    ((isCancerDetectUser || cancerDetectClicked) ?
      DASHBOARD_ROUTES.filter((route) => !route.hideIconForCancerDetect)
      : DASHBOARD_ROUTES
    ).filter((route) => showFormulaRecs || !route.hideIconForDisabledFormulaRecs);
  return filteredRoutes.map(route => (
    <li key={route.label}>
      <SidebarMenuButton asChild>
        <DashboardLayoutNavItem
          to={route.to}
          icon={route.icon}
          label={route.label}
          activeIcon={route.activeIcon}
        />
      </SidebarMenuButton>
    </li>
  ));
};

const renderedSubscriptionPanelLinks = (
  kitType: string,
  preRnRKitType: string,
  isCancerDetectUser: boolean,
  isWellNessKitUser: boolean,
  onClickRoute: (labelType: string) => void,
  isCancerDetectClicked: boolean,
) => {

  const SUBSCRIPTIONS_ROUTES = [
    { label: getKitName(kitType) || getKitName(preRnRKitType), icon: '', to: '/', active: !isCancerDetectClicked, hideOption: !isWellNessKitUser },
    { label: 'CancerDetect', icon: '', to: '/cancer-detect', active: isCancerDetectClicked, hideOption: !isCancerDetectUser },
  ];

  const resultedRoutes = SUBSCRIPTIONS_ROUTES.filter((route) => !route.hideOption)

  return resultedRoutes.map((route) => (
    <li key={route.label}>
      <SidebarMenuButton asChild>
        <Link
          to={route.to}
          className="typography-body gap-2 justify-start"
          onClick={() => onClickRoute(route?.label)}
        >
          {route.label} {route.active && <CheckIcon size={24} />}
        </Link>
      </SidebarMenuButton>
    </li>
  ));
}

export const DashboardLayoutSidebar = (props: DashboardLayoutSidebarProps) => {
  const { bootupState } = useBootup();
  const { isCancerDetectClicked, setIsCancerDetectClicked } = useCancerDetectUser();
  const { kitDetailsData, CDkitDetailsData, kitType, preRnRKitType } = bootupState || {};
  const { admin: isAdmin } = usePeacockAcl();
  const orgs = useContextStore((s) => s.orgs);
  const patientOrg = useContextStore((s) => s.patientOrg);
  const selectConsumer = useContextStore((s) => s.selectConsumer);
  const showFormulaRecs = useShowFormulaRecs();
  // Pro users (partner-org context) get CancerDetect folded into the context
  // switcher dropdown, so the "My Tests" link list is only for non-Pro users
  // holding a regular kit + CancerDetect.
  const isProUser = orgs.length > 0;
  const hideSideBarOptionsForCancerDetectUsers = Boolean(CDkitDetailsData && !kitDetailsData);
  const displayTestsSection = !isProUser && Boolean(CDkitDetailsData && kitDetailsData);
  const isNoKitDetailsAvailable = (obj) => !obj || Object.keys(obj).length === 0;

  const onClickRoute = (labelType: string) => {
    if (labelType === 'CancerDetect') {
      setIsCancerDetectClicked(true);
    } else {
      setIsCancerDetectClicked(false);
    }
  };

  // Check if both kit types values are null and not display the sidebar.
  if (isNoKitDetailsAvailable(kitDetailsData) && isNoKitDetailsAvailable(CDkitDetailsData)) {
    return;
  }

  return (
    <Sidebar variant="sidebar" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild>
              <Link
                to={HOME_PATH}
                onClick={() => {
                  // Home exits CD only from the consumer context: clear the flag
                  // AND re-scope activeKitId back to the wellness kit, else home
                  // v2 renders against the CD kit the switcher set. Never touch
                  // context for an org-context user — logo just navigates home.
                  if (patientOrg || !isCancerDetectClicked) return;
                  setIsCancerDetectClicked(false);
                  selectConsumer();
                }}
              >
                <Image width={180} height={24} src={LOGO_WHITE} alt="Viome" />
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        <ul>
          {renderedSidebarRoutes({
            isCancerDetectUser: hideSideBarOptionsForCancerDetectUsers,
            cancerDetectClicked: isCancerDetectClicked,
            showFormulaRecs,
          })}
          {isAdmin && (
            <li>
              <SidebarMenuButton asChild>
                <DashboardLayoutNavItem
                  to={ACL_ADMIN_URL}
                  external
                  icon={Shield}
                  activeIcon={Shield}
                  label="Admin"
                />
              </SidebarMenuButton>
            </li>
          )}
        </ul>
      </SidebarContent>

      <SidebarFooter className="text-primary-foreground gap-4 flex flex-col">
        {displayTestsSection && (
          <>
            <span className="mx-3 typography-title font-serif italic border-b border-stroke-static py-4">
              My Tests
            </span>

            <ul>
              {renderedSubscriptionPanelLinks(
                kitType,
                preRnRKitType,
                Boolean(CDkitDetailsData),
                Boolean(kitDetailsData),
                onClickRoute,
                isCancerDetectClicked,
              )}
            </ul>
          </>
        )}
        <DashboardLayoutContextSwitcher />
      </SidebarFooter>
    </Sidebar>
  );
};
